import { addDays, weekdayOf, type DateKey } from './time';

export type HabitSchedule = 'daily' | 'weekdays' | 'weekly';

export interface HabitLike {
  id: string;
  schedule: HabitSchedule;
  target_per_week: number;
}

export interface HabitLogLike {
  habit_id: string;
  date: DateKey;
  done: boolean;
}

/** Whether a day "counts" for the habit. Weekly habits can be done on any day. */
export function habitAppliesOn(schedule: HabitSchedule, date: DateKey): boolean {
  if (schedule !== 'weekdays') return true;
  const d = weekdayOf(date);
  return d !== 0 && d !== 6;
}

export function doneDates(logs: HabitLogLike[], habitId: string): Record<string, true> {
  const out: Record<string, true> = {};
  for (const l of logs) if (l.habit_id === habitId && l.done) out[l.date] = true;
  return out;
}

/** First day of the week containing `date`. */
export function startOfWeek(date: DateKey, weekStartsOn: 0 | 1): DateKey {
  const back = (weekdayOf(date) - weekStartsOn + 7) % 7;
  return addDays(date, -back);
}

const MAX_LOOKBACK = 400;

/**
 * Current streak, in days for daily/weekdays habits and in weeks for weekly ones.
 * Unlike a naive day count, days the schedule doesn't cover (weekends for a weekdays habit) are
 * skipped instead of breaking the streak, and today/this week not yet done doesn't break it either.
 */
export function streak(habit: HabitLike, logs: HabitLogLike[], today: DateKey, weekStartsOn: 0 | 1 = 0): number {
  const done = doneDates(logs, habit.id);
  if (habit.schedule === 'weekly') {
    const target = Math.max(1, Math.min(7, habit.target_per_week));
    let week = startOfWeek(today, weekStartsOn);
    let n = 0;
    const countWeek = (start: DateKey) => {
      let c = 0;
      for (let i = 0; i < 7; i++) if (done[addDays(start, i)]) c++;
      return c;
    };
    // The current week only counts once its target is met; otherwise start from last week.
    if (countWeek(week) >= target) n++;
    week = addDays(week, -7);
    for (let i = 0; i < MAX_LOOKBACK / 7 && countWeek(week) >= target; i++) {
      n++;
      week = addDays(week, -7);
    }
    return n;
  }
  let cursor = today;
  if (!done[cursor]) cursor = addDays(cursor, -1);
  let n = 0;
  for (let i = 0; i < MAX_LOOKBACK; i++) {
    if (!habitAppliesOn(habit.schedule, cursor)) {
      cursor = addDays(cursor, -1);
      continue;
    }
    if (!done[cursor]) break;
    n++;
    cursor = addDays(cursor, -1);
  }
  return n;
}

export function streakLabel(habit: HabitLike, n: number): string {
  if (habit.schedule === 'weekly') return `${n} ${n === 1 ? 'week' : 'weeks'}`;
  return `${n} ${n === 1 ? 'day' : 'days'}`;
}

/** Completions in the week containing `today` (for "2 of 3 this week"). */
export function weekProgress(habit: HabitLike, logs: HabitLogLike[], today: DateKey, weekStartsOn: 0 | 1 = 0): { done: number; target: number } {
  const done = doneDates(logs, habit.id);
  const start = startOfWeek(today, weekStartsOn);
  let n = 0;
  let scheduled = 0;
  for (let i = 0; i < 7; i++) {
    const d = addDays(start, i);
    if (done[d]) n++;
    if (habitAppliesOn(habit.schedule, d)) scheduled++;
  }
  return { done: n, target: habit.schedule === 'weekly' ? habit.target_per_week : scheduled };
}

export type HeatCell = { date: DateKey; state: 'done' | 'missed' | 'off' | 'future' };

/**
 * Five whole weeks ending with the current one, oldest first, as Daybook's heatmap shows.
 * `off` = not scheduled that day; `missed` = scheduled and not done; weekly habits never "miss" a day.
 */
export function heatmap(habit: HabitLike, logs: HabitLogLike[], today: DateKey, weekStartsOn: 0 | 1 = 0, weeks = 5): HeatCell[][] {
  const done = doneDates(logs, habit.id);
  const first = addDays(startOfWeek(today, weekStartsOn), -7 * (weeks - 1));
  const out: HeatCell[][] = [];
  for (let w = 0; w < weeks; w++) {
    const row: HeatCell[] = [];
    for (let d = 0; d < 7; d++) {
      const date = addDays(first, w * 7 + d);
      let state: HeatCell['state'];
      if (date > today) state = 'future';
      else if (done[date]) state = 'done';
      else if (habit.schedule === 'weekly' || !habitAppliesOn(habit.schedule, date)) state = 'off';
      else state = date === today ? 'off' : 'missed';
      row.push({ date, state });
    }
    out.push(row);
  }
  return out;
}

export const SCHEDULE_LABEL: Record<HabitSchedule, string> = { daily: 'Every day', weekdays: 'Weekdays', weekly: 'Weekly' };
